import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { App } from "@capacitor/app";
import { Platform } from "@ionic/angular";
import { Store } from "@ngrx/store";
import moment from "moment";
import { Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { ClientSettings } from "src/model/class/client-settings";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { MomentFormats } from "src/model/class/general classes/moment-formats";
import { Plant } from "src/model/class/general classes/plant";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { DataLogger } from "src/model/interface/data-logger";
import { iDevice } from "src/model/interface/iDevice";
import { clientSettings } from "src/state/selectors/client-settings.selectors";
import { selectedPlant } from "src/state/selectors/plant.selectors";

/**
 * Handle logging on custom remote servere and locally
 */
@Injectable({
  providedIn: "root",
})
export class LoggerService {
  private _logContext: string = "Logger.service";

  /**
   * Reference to the singleton instance, assigned on construction. It provides a logging
   * entry point ({@link logStatic}) for contexts where Angular dependency injection is not
   * available (plain model/tag classes, NgRx meta-reducers, ...).
   */
  private static _instance: LoggerService = null;

  //#region Subcriptions to "_store.select(...)" Observables
  private _selectClientSettingsUserSub: Subscription = null;
  private _selectSelectedPlantUserSub: Subscription = null;
  //#endregion

  private _loggerIsReady: boolean = false;
  private _currentPlant: Plant = null;
  private _currentVersion: string = "";
  private _clientSettings: ClientSettings = null;
  private _deviceSerialNumber: string = "";

  constructor(
    private _http: HttpClient,
    private _platform: Platform,
    private _store: Store
  ) {
    LoggerService._instance = this;
    this.loadCurrentApplicationVersion();
  }

  /**
   * Static logging entry point for contexts that cannot inject {@link LoggerService}
   * (plain model/tag classes, NgRx meta-reducers, ...). Delegates to the singleton
   * instance; it is a no-op until the service has been constructed by Angular.
   * @param level level to log
   * @param context current context class name
   * @param className class name of log
   * @param procedureName procedure name log
   * @param message custom/exception message
   */
  public static logStatic(
    level: LogLevel,
    context: string,
    className: string,
    procedureName: string,
    message: string
  ): void {
    LoggerService._instance?.log(level, context, className, procedureName, message);
  }

  //#region Public setters.
  /*These are needed in order to avoid any risk of circular dependencies with other services. 
  The data these other services provide are instead given through the setters.
   */
  public set deviceInfo(deviceInfo: iDevice) {
    this._deviceSerialNumber = deviceInfo.SerialNumber;
  }

  //#endregion

  ngOnDestroy() {
    this.unsubscribeStoreSelect();
  }

  /**
   * Execute public logging function to log always
   *
   * @param level level to log
   * @param context Current contect class name
   * @param className class name of log
   * @param procedureName procedure name log
   * @param message custom/exception message;
   */
  public log(
    level: LogLevel,
    context: string,
    className: string,
    procedureName: string,
    message: string
  ): boolean {
    if (this.isLoggingReadyToRumble()) {
      if (level >= this._clientSettings?.remoteLogLevel) {
        if (!environment.production) {
          const currentLog: string = this.getCurrentLogString(
            level,
            context,
            className,
            procedureName,
            message
          );

          this.logOnNativeConsole(level, currentLog);
        }

        /* ATTENTION */
        /* This code shall be use for development reason to log on Android/iOS device */
        if (this._platform.is("android") || this._platform.is("ios")) {
          const currentLog: string = this.getCurrentLogString(
            level,
            context,
            className,
            procedureName,
            message
          );

          this.logOnNativeConsole(level, currentLog);
        }

        if (this._clientSettings?.remoteLogEnable) {
          this.logOnRemoteServer(
            level,
            context,
            className,
            procedureName,
            message
          );
        }
      }
      return true;
    } else {
      return false;
    }
  }

  /**
   * Get current log level string //
   *
   * @param level level to log
   * @param className class name of log
   * @param procedureName procedure name log
   * @param message custom/exception message;
   * @returns
   */
  private getCurrentLogString(
    level: LogLevel,
    context: string,
    className: string,
    procedureName: string,
    message: string
  ): string {
    let currentLog: string = this.getLogStartByLogType(level);
    let classNameContext = className;

    if (className !== context) {
      classNameContext = context + " - " + className;
    }

    return (
      currentLog +
      " " +
      classNameContext +
      " - " +
      procedureName +
      " - " +
      message
    );
  }

  /**
   * Get log start by log type
   *
   * @param level Current log level set
   */
  private getLogStartByLogType(level: LogLevel): string {
    let logStart: string = "";

    switch (level) {
      case LogLevel.LOG_INFO:
        logStart = ">> INFO: ";
        break;
      case LogLevel.LOG_WARNING:
        logStart = ">> WARNING: ";
        break;
      case LogLevel.LOG_ERROR:
        logStart = ">> ERROR: ";
        break;
      default:
        break;
    }

    return logStart;
  }

  /**
   * Log message in native console (browser or other)
   *
   * @param level Level to log
   * @param message Message to log
   */
  private logOnNativeConsole(level: LogLevel, message: string): void {
    // Intentional direct console usage: this is the single native-console sink of the
    // logger. All application code must log through LoggerService, never the native console directly.
    switch (level) {
      case LogLevel.LOG_ERROR:
        console.error(message);
        break;
      case LogLevel.LOG_WARNING:
        console.warn(message);
        break;
      default:
        console.log(message);
        break;
    }
  }

  /**
   * Log specific log on remote server
   *
   * @param level level to log
   * @param context Current contect class name
   * @param className class name of log
   * @param procedureName procedure name log
   * @param msg custom/exception message;
   */
  private logOnRemoteServer(
    level: LogLevel,
    context: string,
    className: string,
    procedureName: string,
    msg: string
  ): void {
    try {
      const objectToLog: DataLogger = this.getMessageLogObject(
        level,
        context,
        className,
        procedureName,
        msg
      );

      this._http
        .post(
          environment.remoteServerLogAddress + AppSettings.API_REMOTE_URL_LOG,
          objectToLog
        )
        .toPromise();
    } catch (exception) {
      // Intentional direct console usage: routing this failure back through the logger
      // would recurse into logOnRemoteServer and risk an infinite loop.
      console.error(exception);
    }
  }

  /**
   *  Compose object to execute remote log
   *
   * @param level level to log
   * @param className class name of log
   * @param procedureName procedure name log
   * @param msg custom/exception message;
   * @returns
   */
  private getMessageLogObject(
    level: LogLevel,
    context: string,
    className: string,
    procedureName: string,
    msg: string
  ): DataLogger {
    let currentPlantName: string = "";
    let deviceName: string = "";
    let serialNumber: string = "";

    if (this._currentPlant) {
      currentPlantName = this._currentPlant.PlantName;
    }

    if (this._clientSettings) {
      deviceName = this._clientSettings.deviceName;
    }

    if (this._deviceSerialNumber) {
      serialNumber = this._deviceSerialNumber;
    }

    return {
      service: {
        plantName: currentPlantName,
        deviceName,
        serialNumber,
        version: this._currentVersion,
        serviceName: "BIGStudio3",
      },
      logLevel: level,
      classContext: context,
      className: className,
      methodName: procedureName,
      exceptionMessage: msg,
      exceptionMessageCustom: msg,
      signature: {},
      date: moment().format(MomentFormats.MOMENT_DATETIME_ISO_8601_FORMAT),
      dateUTC: moment().utc().format(MomentFormats.MOMENT_DATETIME_ISO_8601_FORMAT),
    };
  }

  /**
   * Get current plant data
   */
  private loadCurrentPlant(): void {
    this._selectSelectedPlantUserSub = this._store
      .select(selectedPlant)
      .subscribe((currentPlant: Plant) => {
        this._currentPlant = currentPlant;
      });
  }

  /**
   * Load current app version if possibile
   */
  private async loadCurrentApplicationVersion(): Promise<void> {
    if (this._platform.is("android") || this._platform.is("ios")) {
      this._currentVersion = (await App.getInfo()).version;
    }
  }

  /**
   * Subscribe to current client settings
   */
  private loadCurrentClientSetting(): void {
    //this._settingService.clientSettings
    this._selectClientSettingsUserSub = this._store
      .select(clientSettings)
      .pipe(filter((clientSettings) => clientSettings !== null))
      .subscribe((clientSettings: ClientSettings) => {
        this._clientSettings = clientSettings;
      });
  }

  /**
   * This method checks if "Logger.service" is ready to work. This method is structured so that it shall be called ONLY ONCE.
  
   * @returns If EACH AND EVERY of the public setters (needed to avoid circular dependencies caused by direct injection of some services) have been called AT LEAST ONE TIME, 
   * it returns TRUE. Otherwise, it returns FALSE.
   */
  private isLoggingReadyToRumble(): boolean {
    if (!this._loggerIsReady) {
      this.loadCurrentPlant();
      this.loadCurrentClientSetting();

      this._loggerIsReady = true;
    }

    return this._loggerIsReady;
  }

  //#region Unsubscribe from "_store.selec(...)" Observable
  /**
   * This method handles the unsunìbscription from the Observables returned by the "_store.select(...)" invocations.
   * It SHOULD BE CALLED EACH TIME THE DATA STREAMS ARE NOT NEEDED ANYMORE; this means in:
   *
   * * OnDestroy() [Components/Services]
   * * OnViewWillLeave() [Pages]
   */
  private unsubscribeStoreSelect(): void {
    if (this._selectClientSettingsUserSub) {
      this._selectClientSettingsUserSub.unsubscribe();
    }

    if (this._selectSelectedPlantUserSub) {
      this._selectSelectedPlantUserSub.unsubscribe();
    }
  }
  //#endregion
}
